import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { knownRole } from './access';

// WHAT WANTS YOU — the few counts that ask for a person's hand, keyed by the
// page they live on (`<section>/<page>`), so the section column can carry
// them on its tiles and Home on its figures:
//   graph/review       proposals and questions waiting
//   people/friends     people asking to be friends         (personal)
//   people/following   people asking to follow you         (personal)
//   groups/invitations groups inviting you                (personal)
//   people/invitations invitations this group has out     (a group's admins)
//   plugins/installed  plugins installed here whose lifecycle needs a hand (a
//                      group's: its admins')
//   plugins/runs       runs an app or agent asked for, waiting for your yes (a
//                      group's: a member's own, an admin's all)
//   activity/inbox     the inbox's unread (passed in; NotificationContext polls it)
// Read when the space changes, and again only when the counts are STALE: the
// inbox says something may have changed (`stamp`, NotificationContext's
// attentionStamp — a poll brought something new, or an ask was answered), or a
// section change finds the last read older than STALE_MS. Moving between sections
// used to cost four requests every time. A failed read counts nothing.

export const STALE_MS = 60000;

// WHAT "WAITING" MEANS, once: Home's figure, the section tile and Review's
// Waiting lane all count by these. Home counted PENDING alone, so a proposal
// READY_FOR_APPROVAL sat in Review's lane and in no count on Home.
export const PROPOSAL_WAITING = ['PENDING', 'READY_FOR_APPROVAL'];
export const QUESTION_WAITING = ['PENDING'];
const waiting = (rows, statuses) => (rows || []).filter((r) => statuses.includes(String(r?.status || 'PENDING').toUpperCase())).length;

// A PLUGIN THAT WANTS YOU: set up here and not Off (a hold you chose is not a
// want), and still Setting up (sign in, keys, settings, turning it on) or with
// a tool that can't run — the lifecycle GET /plugins answers per plugin for
// the space (plugin-lifecycle.md §3). The same rule as
// pages/plugins/data/items.js needsYou.
const STUCK = ['failing', 'paused', 'needs_realm'];
export const pluginWants = (lc) => !!lc?.installed && lc.state !== 'paused'
    && (lc.state !== 'ready' || (lc.tools || []).some((t) => STUCK.includes(t?.schedule?.state)));
const pluginsWanting = (groupId) => Promise.resolve()
    .then(() => api.getPlugins(groupId ? { group_id: groupId } : {}))
    .then((r) => (r?.plugins || []).filter((p) => pluginWants(p?.lifecycle)).length)
    .catch(() => 0);

// RUNS WAITING FOR YOU: parked runs an app or agent asked for (GET
// /plugin-requests?status=pending, the group's twin in a group). An older
// server without the route counts none.
export const pluginRequestsPath = (groupId) => `${groupId ? `/groups/${groupId}` : ''}/plugin-requests?status=pending`;
const pendingOf = (r) => (Array.isArray(r) ? r : r?.requests || []).filter((x) => (x?.status || 'pending') === 'pending').length;
const runsWaiting = (groupId) => Promise.resolve()
    .then(() => api.request(pluginRequestsPath(groupId)))
    .then(pendingOf)
    .catch(() => 0);

async function readCounts(groupId) {
    const out = {};
    if (groupId) {
        // a member cannot answer what a group's plugin needs: counted for its admins
        const admin = knownRole(groupId) !== 'member';
        const [p, inv, plugins, runs] = await Promise.all([
            api.request(`/groups/${groupId}/proposals?limit=200&offset=0`).catch(() => null),
            api.getGroupInvitations?.(groupId).catch(() => null),
            admin ? pluginsWanting(groupId) : 0,
            runsWaiting(groupId),
        ]);
        out['graph/review'] = waiting(p?.proposals, PROPOSAL_WAITING);
        out['people/invitations'] = (inv?.invitations || []).filter((i) => (i.status || 'pending') === 'pending').length;
        out['plugins/installed'] = plugins;
        out['plugins/runs'] = runs;
        return out;
    }
    const [p, q, follows, inv, plugins, runs] = await Promise.all([
        api.getProposals({ limit: 200, offset: 0 }).catch(() => null),
        api.getQuestions({ limit: 200 }).catch(() => null),
        api.getIncomingFollows({ status: 'pending', limit: 100 }).catch(() => null),   // friend and follow requests
        api.getIncomingInvitations().catch(() => null),
        pluginsWanting(null),
        runsWaiting(null),
    ]);
    out['plugins/installed'] = plugins;
    out['plugins/runs'] = runs;
    out['graph/review'] = waiting(p?.proposals, PROPOSAL_WAITING) + waiting(q?.questions, QUESTION_WAITING);
    const asks = (follows?.follows || []).filter((f) => f.status === 'pending');
    out['people/friends'] = asks.filter((f) => f.relationship_type === 'friend_request').length;
    out['people/following'] = asks.filter((f) => f.relationship_type !== 'friend_request').length;
    out['groups/invitations'] = (inv?.invitations || []).filter((i) => (i.status || 'pending') === 'pending').length;
    return out;
}

export function useAttention(groupId, { inbox = 0, section = null, off = false, stamp = 0 } = {}) {
    const [counts, setCounts] = useState({});
    const read = useRef({ key: null, at: 0 });
    useEffect(() => {
        if (off) return undefined; // an app preview is not the person's own space
        const key = `${groupId || ''}|${stamp}`;
        // the same space and nothing marked stale: a section change alone reads
        // again only once the last read has aged past STALE_MS
        if (read.current.key === key && Date.now() - read.current.at < STALE_MS) return undefined;
        const mine = { key, at: Date.now() };
        read.current = mine;
        // kept unless a later read has started — a section change that skips the
        // read must not drop the one still in flight
        readCounts(groupId).then((c) => { if (read.current === mine) setCounts(c); }).catch(() => {});
        return undefined;
    }, [groupId, section, off, stamp]);
    return { ...counts, 'activity/inbox': inbox };
}
